import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, formatDate, formatNumber } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import {
  MipsApiService,
  SubmissionCheckResult,
  SubmissionCheckRow,
} from '@services/mips-api.service';
import { parseProblemDetailsError } from '../../utils/api-error.util';
import { downloadBlob } from '../../utils/blob-viewer.util';

export interface MipsSubmissionCheckDialogData {
  submissionId: number;
}

interface SubmissionCheckDisplayRow {
  row: SubmissionCheckRow;
  isFirstOfMeasure: boolean;
  cmsProcessingStatus: string | null;
  cmsMessages: [string, string][];
  prettySentJson: string | null;
  prettyCmsJson: string | null;
}

@Component({
  selector: 'app-mips-submission-check-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
  ],
  templateUrl: './mips-submission-check-dialog.component.html',
  styleUrl: './mips-submission-check-dialog.component.css',
})
export class MipsSubmissionCheckDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<MipsSubmissionCheckDialogComponent>);
  readonly data = inject<MipsSubmissionCheckDialogData>(MAT_DIALOG_DATA);
  private readonly mipsApiService = inject(MipsApiService);

  isLoading = false;
  isDownloading = false;
  errorMessage: string | null = null;
  result: SubmissionCheckResult | null = null;
  displayedRows: SubmissionCheckDisplayRow[] = [];
  shouldShowMismatchesFirst = true;
  shouldShowRawJson = false;
  private hasRawJson = false;
  private readonly expandedMeasureIds = new Set<string>();

  ngOnInit(): void {
    this.loadCheck();
  }

  loadCheck(): void {
    const shouldIncludeRawJson = this.shouldShowRawJson;
    this.isLoading = true;
    this.errorMessage = null;
    this.result = null;
    this.displayedRows = [];

    this.mipsApiService.getSubmissionCheck(this.data.submissionId, shouldIncludeRawJson).subscribe({
      next: (result) => {
        this.result = result;
        this.hasRawJson = shouldIncludeRawJson;
        this.updateDisplayedRows();
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = parseProblemDetailsError(error, 'Failed to load submission check.');
        this.isLoading = false;
      },
    });
  }

  onMismatchesFirstChange(shouldShowMismatchesFirst: boolean): void {
    this.shouldShowMismatchesFirst = shouldShowMismatchesFirst;
    this.updateDisplayedRows();
  }

  onRawJsonChange(shouldShowRawJson: boolean): void {
    this.shouldShowRawJson = shouldShowRawJson;
    if (shouldShowRawJson) {
      this.loadCheck();
    }
  }

  onDownload(): void {
    if (this.hasRawJson && this.result) {
      this.saveResult(this.result);
      return;
    }

    this.isDownloading = true;
    this.mipsApiService.getSubmissionCheck(this.data.submissionId, true).subscribe({
      next: (result) => {
        this.saveResult(result);
        this.isDownloading = false;
      },
      error: (error) => {
        this.errorMessage = parseProblemDetailsError(error, 'Failed to download submission check.');
        this.isDownloading = false;
      },
    });
  }

  hasDetails(displayRow: SubmissionCheckDisplayRow): boolean {
    const hasRawJsonToShow =
      this.shouldShowRawJson && !!(displayRow.prettySentJson || displayRow.prettyCmsJson);
    return !!displayRow.cmsProcessingStatus || displayRow.cmsMessages.length > 0 || hasRawJsonToShow;
  }

  isExpanded(displayRow: SubmissionCheckDisplayRow): boolean {
    return this.expandedMeasureIds.has(displayRow.row.measureId);
  }

  toggleExpanded(displayRow: SubmissionCheckDisplayRow): void {
    const measureId = displayRow.row.measureId;
    if (this.expandedMeasureIds.has(measureId)) {
      this.expandedMeasureIds.delete(measureId);
    } else {
      this.expandedMeasureIds.add(measureId);
    }
  }

  getMeasureLabel(row: SubmissionCheckRow): string {
    return row.measureCode ?? row.measureId;
  }

  hasSeparateMeasureId(row: SubmissionCheckRow): boolean {
    return !!row.measureCode && row.measureCode !== row.measureId;
  }

  formatCount(value: number | null): string {
    return value != null ? formatNumber(value, 'en-US') : '—';
  }

  formatFraction(numerator: number | null, denominator: number | null): string {
    if (numerator == null && denominator == null) return '—';
    return `${this.formatCount(numerator)} / ${this.formatCount(denominator)}`;
  }

  formatRate(value: number | null): string {
    return value != null ? `${formatNumber(value, 'en-US', '1.2-2')}%` : '—';
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private updateDisplayedRows(): void {
    const rows = this.result?.rows ?? [];
    const orderedRows = this.shouldShowMismatchesFirst ? this.sortMismatchesFirst(rows) : rows;

    this.displayedRows = orderedRows.map((row, rowIndex) => {
      const isFirstOfMeasure =
        rowIndex === 0 || orderedRows[rowIndex - 1].measureId !== row.measureId;
      return {
        row,
        isFirstOfMeasure,
        cmsProcessingStatus: isFirstOfMeasure ? this.toTitleCase(row.cms?.processingStatus ?? null) : null,
        cmsMessages: isFirstOfMeasure && row.cms ? Object.entries(row.cms.messages) : [],
        prettySentJson: isFirstOfMeasure ? this.prettyPrintJson(row.sentJson) : null,
        prettyCmsJson: isFirstOfMeasure ? this.prettyPrintJson(row.cmsJson) : null,
      };
    });
  }

  private sortMismatchesFirst(rows: SubmissionCheckRow[]): SubmissionCheckRow[] {
    const mismatchedMeasureIds = new Set(
      rows.filter((row) => !row.isMatch).map((row) => row.measureId)
    );
    return [
      ...rows.filter((row) => mismatchedMeasureIds.has(row.measureId)),
      ...rows.filter((row) => !mismatchedMeasureIds.has(row.measureId)),
    ];
  }

  private toTitleCase(text: string | null): string | null {
    if (!text) return null;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  private prettyPrintJson(json: string | null): string | null {
    if (!json) return null;
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  }

  private saveResult(result: SubmissionCheckResult): void {
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const timestamp = formatDate(new Date(), 'yyyyMMdd-HHmm', 'en-US');
    downloadBlob(blob, `submission-check-${this.data.submissionId}-${timestamp}.json`);
  }
}
